import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  TrendingDown,
  Share2,
  Compass,
  FileText,
  Search,
  Phone,
  Sparkles,
  CheckCircle2,
  ShieldCheck,
  PlayCircle,
  Mail,
  MessageSquare,
  Heart,
  ShoppingBag,
  BarChart3,
  Briefcase,
  Crown,
  Star,
  Store,
  RefreshCw,
  MapPin,
  Plus,
  Minus,
} from 'lucide-react';
import Layout from '../../components/layout/layout';
import Section from '../../components/ui/section';
import SectionBackground from '../../components/sections/section-background';
import ClientLogosWall from '../../components/sections/ClientLogosWall';
import HubSpotModal from '../../components/ui/hubspot-modal';
import { BreadcrumbSchema, FAQPageSchema } from '../../lib/utils/schema-utils';

const PAGE_URL = 'https://recruteurs.io/cabinet-recrutement-paca/';

const gradientText =
  'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]';
const eyebrowClass =
  'inline-flex items-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-neutral-500 uppercase tracking-widest shadow-sm ring-1 ring-neutral-200 mb-6';
const h2Class =
  'text-4xl md:text-5xl font-bold tracking-tight text-[#30475e] mb-5 leading-[1.08]';
const h3Class = 'text-xl font-semibold text-[#30475e] mb-3 leading-snug';
const bodyClass = 'text-base text-neutral-600 leading-relaxed';
const sectionSubtitleClass =
  'text-lg md:text-xl text-neutral-600 leading-relaxed';

const faqItems = [
  {
    question: 'Recrutez-vous dans toute la région PACA ?',
    answer:
      "Oui. Nous couvrons les six départements PACA : Bouches-du-Rhône (Marseille, Aix), Var (Toulon, Draguignan), Vaucluse (Avignon), Alpes-Maritimes (Nice, Cannes, Sophia Antipolis), Alpes-de-Haute-Provence et Hautes-Alpes selon la nature de la mission. Notre périmètre principal reste les Bouches-du-Rhône — pour les autres départements, nous intervenons sur des postes critiques ou des profils rares.",
  },
  {
    question: 'En quoi une connaissance de la région PACA change-t-elle la qualité du recrutement ?',
    answer:
      "Le marché PACA n'est pas homogène. Marseille a ses codes commerciaux, Aix a son tissu PME tech, Toulon a son écosystème défense/naval, Nice a son tourisme premium et sa finance. Les attentes salariales, la mobilité géographique, les rythmes de travail et les codes culturels varient. Notre approche tient compte de cette réalité — un cabinet parisien qui couvre la PACA \"par défaut\" ne calibre pas correctement.",
  },
  {
    question: 'Quelle est votre différence par rapport à un cabinet de recrutement classique ?',
    answer:
      "Notre Plateforme Parcours Découverte. Avant le premier entretien, chaque candidat traverse un environnement digital sur-mesure qui lui montre la vraie réalité de votre entreprise — culture, équipe, mission, grille de rémunération. Le candidat décide en connaissance de cause. Vous éliminez les no-show, vous divisez la casse des 90 premiers jours, vous fidélisez à 12 mois.",
  },
  {
    question: 'Quels types de profils recrutez-vous en PACA ?',
    answer:
      "Cadres et managers, commerciaux (terrain, KAM, business developers), profils techniques et IT, fonctions support (RH, finance, ADV), retail et distribution spécialisée, industrie et services. Nous intervenons sur des CDI, des management de transition et des missions confidentielles de chasse de tête sur les postes de direction.",
  },
  {
    question: 'Avez-vous des bureaux physiques dans toute la PACA ?',
    answer:
      "Notre adresse opérationnelle est à Cassis (13260). Nous nous déplaçons partout en PACA selon les missions — pas de bureau dans chaque ville, mais une mobilité réelle et une connaissance fine des bassins. Cela nous permet d'avoir une structure légère et de proposer des tarifs alignés sur la valeur livrée, pas sur des frais de structure.",
  },
  {
    question: 'Travaillez-vous avec des PME ou uniquement des grandes entreprises ?',
    answer:
      "Notre cœur de cible est la PME et l'ETI régionales, de 20 à 500 collaborateurs. Nous travaillons aussi avec des scale-ups et des entreprises en transformation. Pour les très grands comptes, nous intervenons sur des missions ciblées (postes critiques, contextes sensibles) en collaboration avec leurs équipes RH internes.",
  },
  {
    question: 'Quels sont vos tarifs pour un recrutement en PACA ?',
    answer:
      "Sur le marché : 15 % à 30 % du salaire annuel brut chargé (7 500 € à 15 000 € pour un poste à 50 k€). Pour les profils rares ou de direction, le fee peut dépasser 20 000 €. Deux modèles disponibles : avec retainer (engagement initial qui inclut la production du Parcours Découverte) ou au succès uniquement (production Parcours facturée séparément). Tarifs présentés en transparence dès le premier appel.",
  },
  {
    question: 'Cabinet de recrutement PACA : quelle garantie ?',
    answer:
      "Une garantie chiffrée et opposable : replacement gratuit en cas de rupture dans les 6 mois suivant l'embauche, diagnostic post-mortem honnête (où ça a vraiment lâché), et 30 % du fee restitué si vous ne souhaitez pas de replacement. Pour que la garantie soit réelle, pas un argument commercial.",
  },
  {
    question: 'Recruter à Sophia Antipolis ou Nice, vous savez faire ?',
    answer:
      "Oui. Sophia Antipolis concentre un écosystème tech R&D significatif, Nice a sa finance et son tourisme premium. Nos missions sur les Alpes-Maritimes sont principalement sur des profils tech/IT et des postes de direction. La distance Aix-Nice ne nous arrête pas — la qualité du brief et l'usage des outils IA pour le sourcing compensent l'éloignement géographique sur la phase amont.",
  },
];

const brandRiskStats = [
  {
    icon: Share2,
    metric: '72 %',
    title: 'partagent leur expérience candidat',
    description:
      "Chaque candidat de votre process en parle. À ses pairs, à ses collègues, sur LinkedIn, sur Glassdoor. Que vous l'embauchiez ou non. Source : Talent Board.",
  },
  {
    icon: TrendingDown,
    metric: '64 %',
    title: 'arrêtent d’acheter chez la marque',
    description:
      "Les candidats déçus ne vous oublient pas — ils boycottent. Pour une marque B2C en PACA (commerce, retail, hospitality, tourisme), le coût peut atteindre plusieurs centaines de milliers d'euros par an. Source : Talent Board.",
  },
  {
    icon: AlertTriangle,
    metric: '81 %',
    title: 'blacklistent une marque qui les ghoste',
    description:
      "Une candidature sans réponse, un process qui s'éteint en silence : c'est un client, un futur candidat et son réseau qui partent définitivement. Source : étude marché 2024.",
  },
];

const methodSteps = [
  {
    icon: FileText,
    title: 'Cadrage business du besoin',
    description:
      "Pas une fiche de poste générique : un diagnostic d'enjeu. Pourquoi ce poste, pour quel résultat, dans quel contexte managérial, avec quels critères de réussite mesurables.",
  },
  {
    icon: Compass,
    title: 'Réécriture engageante du brief candidat',
    description:
      "Nous transformons votre fiche de poste en mission claire, honnête, attirante. Le candidat doit comprendre POURQUOI ce poste vaut son temps avant de comprendre quoi y faire.",
  },
  {
    icon: Search,
    title: 'Sourcing chasse de tête + IA',
    description:
      "Approche directe sur les profils passifs PACA, scoring sémantique IA pour élargir intelligemment, qualification fine. La chasse senior pilote, l'IA accélère.",
  },
  {
    icon: Phone,
    title: 'Pré-qualification soignée',
    description:
      "Chaque candidat reçoit un appel de pré-qualification respectueux et structuré, en 30 minutes. C'est souvent leur premier contact avec votre entreprise — nous le traitons comme tel.",
  },
  {
    icon: Sparkles,
    title: 'Plateforme Parcours Découverte',
    description:
      "Les candidats short-listés traversent votre Parcours Découverte. Ils découvrent votre culture, votre équipe, votre quotidien réel, votre rémunération. Ils décident en connaissance de cause.",
  },
  {
    icon: CheckCircle2,
    title: 'Signature et ré-engagement',
    description:
      "Le candidat retenu se ré-engage explicitement à la sortie du Parcours. Plus de signature à l'aveugle, plus de no-show au jour J, plus de rétractation au dernier moment.",
  },
  {
    icon: ShieldCheck,
    title: 'Intégration et suivi 90 jours',
    description:
      "Onboarding personnalisé dès la signature, points de feedback RH et manager, objectifs suivis semaine par semaine. Le recrutement n'est solide qu'au 90e jour — pas à la signature.",
  },
];

const candidateCommitments = [
  {
    icon: Mail,
    title: 'Réponse écrite à 100 % des candidatures',
    description:
      "Sous 48 heures ouvrées. Aucun ghosting, jamais. Même un refus est une réponse soignée — c'est ce qui fait la différence dans la mémoire des candidats.",
  },
  {
    icon: MessageSquare,
    title: 'Brief honnête sur le poste, l’équipe, le contexte',
    description:
      "Pas de marketing maquillé, pas de promesse vague. Le candidat reçoit la vraie réalité — y compris les contraintes — pour pouvoir décider en adulte.",
  },
  {
    icon: RefreshCw,
    title: 'Debrief structuré à chaque étape',
    description:
      "Le candidat sait pourquoi il avance, ou pourquoi il s'arrête. Avec des éléments concrets. Pour qu'il progresse, même s'il n'est pas retenu chez vous.",
  },
  {
    icon: Sparkles,
    title: 'Parcours Découverte avant l’entretien final',
    description:
      "Pas de surprise après la signature, plus de regret six mois après. Le candidat voit la réalité avant de s'engager — et n'avance que s'il est aligné.",
  },
];

const specialites = [
  {
    icon: Briefcase,
    title: 'Recrutement commercial',
    description:
      "Commerciaux terrain, KAM, business developers, directeurs régionaux, directeurs commerciaux. Le marché PACA est exigeant : nous évaluons motivation, projet professionnel et compatibilité culturelle — pas seulement le palmarès chiffré.",
  },
  {
    icon: Crown,
    title: 'Cadres et managers',
    description:
      "Responsables de service, directeurs opérationnels, cadres dirigeants. Approches directes confidentielles sur des profils qui ne candidatent pas spontanément. La discrétion est un prérequis.",
  },
  {
    icon: Store,
    title: 'Retail et distribution spécialisée',
    description:
      "Directeurs de magasin, chefs de secteur, directeurs régionaux retail. Nous comprenons les rythmes et les contraintes du commerce physique en PACA, où la densité de points de vente et la concurrence RH sont fortes.",
  },
  {
    icon: Star,
    title: 'Industrie, services et fonctions support',
    description:
      "Industrie, BTP, services, RH, finance, ADV, juridique. Profils souvent sous-estimés dans leur impact sur la performance — nous leur accordons la même rigueur de qualification que les fonctions cœur de business.",
  },
];

const dividendStats = [
  {
    icon: Heart,
    metric: '88 %',
    text:
      "des candidats avec une bonne expérience deviennent acheteurs des produits — y compris s'ils n'ont pas été retenus. Source : Talent Board.",
  },
  {
    icon: ShoppingBag,
    metric: '× 4,4 M £',
    text:
      "C'est ce que Virgin Media a chiffré comme revenu perdu chaque année à cause de ses candidats refusés qui résiliaient leur abonnement.",
  },
  {
    icon: BarChart3,
    metric: '91 % vs 26 %',
    text:
      "des employeurs admettent l'impact de l'expérience candidat sur leurs ventes — 26 % seulement la mesurent. Le gap est votre opportunité.",
  },
];

const comparatif = [
  {
    critere: 'Plateforme Parcours Découverte',
    recruteurs: 'Environnement digital sur-mesure pour chaque candidat',
    classique: 'Aucun équivalent',
  },
  {
    critere: 'Couverture régionale',
    recruteurs: 'Présence et connaissance fine des 6 départements PACA',
    classique: "Souvent une couverture \"par défaut\" depuis Paris",
  },
  {
    critere: 'Sourcing',
    recruteurs: 'Approche directe + réseau régional + IA',
    classique: 'Jobboards et CVthèque',
  },
  {
    critere: 'Suivi après embauche',
    recruteurs: 'Inclus — points à J+30 et J+90',
    classique: 'Rare ou facturable en option',
  },
  {
    critere: 'Garantie',
    recruteurs: 'Replacement < 6 mois + 30 % fee restitué',
    classique: 'Variable, conditions souvent floues',
  },
];

const guaranteeBullets = [
  {
    icon: RefreshCw,
    title: 'Replacement gratuit en cas de rupture < 6 mois',
    description:
      "Si votre nouvelle recrue rompt son contrat dans les 6 premiers mois, nous relançons un sourcing complet sans frais supplémentaires.",
  },
  {
    icon: FileText,
    title: 'Diagnostic post-mortem honnête',
    description:
      "Nous identifions où ça a vraiment lâché : projection candidat, package, management, contexte business. Pour ne pas refaire la même erreur.",
  },
  {
    icon: ShieldCheck,
    title: '30 % du fee restitué',
    description:
      "Si vous ne souhaitez pas de replacement, nous vous restituons 30 % du fee. Pour que la garantie soit réelle — pas un argument commercial.",
  },
];

// — Section zones avec descriptions sectorielles par ville (spécifique PACA) —
const zonesDetailed = [
  {
    ville: 'Marseille',
    desc: 'Premier bassin économique régional. Commerce, logistique, services, retail, distribution.',
    href: '/cabinet-recrutement-marseille',
  },
  {
    ville: 'Aix-en-Provence',
    desc: "Tissu PME dense. Technologie, services B2B, industrie, Pays d'Aix.",
    href: '/cabinet-recrutement-aix-en-provence',
  },
  {
    ville: 'Toulon',
    desc: 'Défense, naval, services, santé. Profils techniques et cadres spécialisés.',
  },
  {
    ville: 'Avignon',
    desc: 'Logistique, agro-alimentaire, services aux entreprises. Vaucluse et périphérie.',
  },
  {
    ville: 'Nice',
    desc: "Côte d'Azur, tourisme premium, tech, finance, commerce international.",
  },
  {
    ville: 'Aubagne',
    desc: 'Industrie, BTP, services. Axe Marseille-Toulon avec tissu PME significatif.',
  },
  {
    ville: 'Vitrolles',
    desc: 'Zone aéroportuaire et logistique. Profils commerciaux, supply chain, ADV.',
  },
  {
    ville: 'Salon-de-Provence',
    desc: "Industrie, services, agriculture. Périphérie d'Aix avec ses propres dynamiques RH.",
  },
];

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': ['LocalBusiness', 'ProfessionalService'],
  name: 'Recruteurs.io',
  description:
    "Cabinet de recrutement en PACA. Couverture des six départements : Bouches-du-Rhône, Var, Vaucluse, Alpes-Maritimes, Alpes-de-Haute-Provence, Hautes-Alpes. Plateforme Parcours Découverte propriétaire.",
  url: 'https://recruteurs.io',
  email: 'contact@recruteurs.io',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '11 chemin du Mont Gibaou',
    addressLocality: 'Cassis',
    postalCode: '13260',
    addressCountry: 'FR',
  },
  areaServed: [
    { '@type': 'City', name: 'Marseille' },
    { '@type': 'City', name: 'Aix-en-Provence' },
    { '@type': 'City', name: 'Toulon' },
    { '@type': 'City', name: 'Nice' },
    { '@type': 'City', name: 'Avignon' },
    { '@type': 'City', name: 'Aubagne' },
    { '@type': 'City', name: 'Vitrolles' },
    { '@type': 'City', name: 'Salon-de-Provence' },
    { '@type': 'AdministrativeArea', name: 'Bouches-du-Rhône' },
    { '@type': 'AdministrativeArea', name: 'Var' },
    { '@type': 'AdministrativeArea', name: 'Vaucluse' },
    { '@type': 'AdministrativeArea', name: 'Alpes-Maritimes' },
    { '@type': 'AdministrativeArea', name: 'PACA' },
  ],
  priceRange: '€€€',
  logo: 'https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/assets//Recruteurs.io%20Logo%20Final-01.png',
  sameAs: [
    'https://www.linkedin.com/company/recruteurs-io',
    'https://twitter.com/recruteurs_io',
    'https://www.facebook.com/recruteurs.io',
  ],
};

export default function CabinetRecrutementPacaPage() {
  const [isHubSpotModalOpen, setIsHubSpotModalOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <Layout>
      <Helmet>
        <title>Cabinet de recrutement en PACA · Aix, Marseille, Nice</title>
        <meta
          name="description"
          content="Cabinet de recrutement en PACA : Aix, Marseille, Toulon, Nice. Postes critiques pourvus en 6 semaines, garantie 6 mois, méthode Parcours Découverte."
        />
        <link rel="canonical" href={PAGE_URL} />
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      </Helmet>

      <BreadcrumbSchema
        items={[
          { name: 'Accueil', url: 'https://recruteurs.io' },
          { name: 'Cabinet de recrutement en PACA', url: PAGE_URL },
        ]}
      />

      <FAQPageSchema questions={faqItems} />

      {/* HERO — layout centré + image emblématique de la région en fond */}
      <SectionBackground
        page="local-paca"
        section="lp-hero"
        className="relative overflow-hidden bg-gradient-to-br from-[#fff1f2] via-[#fef4e6] to-[#e0f2ff]"
      >
        <div
          aria-hidden
          className="absolute inset-0 z-0 pointer-events-none select-none flex items-center justify-center"
        >
          <img
            src="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/3.png"
            alt=""
            className="w-full h-full object-cover opacity-15 md:opacity-20"
            fetchpriority="high"
            loading="eager"
            decoding="async"
          />
          <div className="absolute inset-0 bg-white/30" />
        </div>

        <section className="relative z-10 min-h-[88vh] flex items-center py-10 md:py-12 lg:py-14">
          <div className="w-full px-[5vw] xl:px-[100px] max-w-[1440px] mx-auto">
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-xs md:text-sm font-semibold text-neutral-500 mb-3 leading-snug uppercase tracking-widest">
                Couverture régionale · 6 départements · Marseille · Aix · Toulon · Nice
              </p>

              <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-[3.5rem] font-title font-extrabold leading-[1.1] pb-1 mb-0">
                <span className="text-[#30475e]">Cabinet de recrutement </span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
                  en PACA
                </span>
              </h1>

              <p className="mt-5 mb-8 text-neutral-800 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
                Recrutement à l’échelle régionale&nbsp;: Bouches-du-Rhône, Var,
                Vaucluse, Alpes-Maritimes. Connaissance fine des spécificités
                sectorielles de chaque bassin.{' '}
                <strong>Plateforme Parcours Découverte</strong> avant signature,
                chasse de tête + IA, garantie replacement. Pas une couverture
                PACA «&nbsp;par défaut&nbsp;» depuis Paris — un ancrage réel.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button
                  onClick={() => navigate('/reservation')}
                  className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-2.5 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-[#d03484]/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
                >
                  Réserver un échange (15 min)
                </button>
                <button
                  onClick={() => navigate('/decouvrir')}
                  className="inline-flex items-center gap-2 rounded-2xl bg-white text-[#30475e] px-6 py-2.5 text-sm font-bold whitespace-nowrap ring-1 ring-neutral-200 hover:ring-neutral-300 hover:-translate-y-0.5 transition-all"
                >
                  Voir le Parcours Découverte
                </button>
              </div>
            </div>
          </div>
        </section>
      </SectionBackground>

      {/* SECTION 2 — Marque employeur en jeu */}
      <SectionBackground page="local-paca" section="lp-stake" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Le vrai coût d’un recrutement mal traité</span>
            <h2 className={h2Class}>
              La PACA est une région où les réseaux pros{' '}
              <span className={gradientText}>se croisent et se parlent vite.</span>
            </h2>
            <p className={sectionSubtitleClass}>
              Sur un poste cadre en PACA, vous allez recevoir entre 50 et 100 candidatures.
              Vous en embaucherez une. Les autres parlent de vous — à leur réseau, à LinkedIn,
              à Glassdoor. Le bouche-à-oreille pro circule particulièrement vite ici.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {brandRiskStats.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className="rounded-[1.75rem] bg-white p-8 ring-1 ring-neutral-200 shadow-sm hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <div className="mb-6 flex items-center justify-between">
                    <div className="h-14 w-14 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white shadow-lg shadow-[#d03484]/20">
                      <Icon className="w-7 h-7" />
                    </div>
                    <p className="text-3xl font-bold text-[#30475e] tracking-tight">{item.metric}</p>
                  </div>
                  <h3 className={h3Class}>{item.title}</h3>
                  <p className={bodyClass}>{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 3 — Bassins d'emploi (zones detailed avec desc sectorielle) */}
      <SectionBackground page="local-paca" section="lp-zones" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Les bassins d’emploi PACA</span>
            <h2 className={h2Class}>
              Chaque ville a ses codes.{' '}
              <span className={gradientText}>Nous les connaissons.</span>
            </h2>
            <p className={sectionSubtitleClass}>
              La PACA n’est pas un marché homogène. Les attentes salariales,
              les rythmes de travail, les codes culturels et les spécialités
              sectorielles varient d’une ville à l’autre. Notre approche tient
              compte de cette réalité.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {zonesDetailed.map((zone, index) => (
              <motion.div
                key={zone.ville}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="rounded-[1.75rem] bg-white p-6 ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white flex-shrink-0 shadow-lg shadow-[#d03484]/20">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#30475e] leading-snug mb-1.5">
                      {zone.href ? (
                        <a
                          href={zone.href}
                          className={`${gradientText} hover:underline underline-offset-4`}
                        >
                          {zone.ville}
                        </a>
                      ) : (
                        zone.ville
                      )}
                    </h3>
                    <p className="text-sm text-neutral-600 leading-relaxed">{zone.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 4 — Méthode 7 temps */}
      <SectionBackground page="local-paca" section="lp-method" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Notre méthode en 7 temps</span>
            <h2 className={h2Class}>
              Recruter mieux, pas juste plus vite.{' '}
              <span className={gradientText}>Exigeant côté business, premium côté candidat.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {methodSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.6, delay: index * 0.07 }}
                  className="group rounded-[1.75rem] bg-white p-8 shadow-sm ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <div className="mb-8 flex items-center justify-between">
                    <div className="h-14 w-14 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white font-bold shadow-lg shadow-[#d03484]/20">
                      0{index + 1}
                    </div>
                    <Icon className="w-6 h-6 text-neutral-300 group-hover:text-[#d03484] transition-colors duration-300" />
                  </div>
                  <h3 className={h3Class}>{step.title}</h3>
                  <p className={bodyClass}>{step.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 5 — Engagements candidat */}
      <SectionBackground page="local-paca" section="lp-cx" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Ce que vivent vos candidats</span>
            <h2 className={h2Class}>
              Quatre promesses concrètes.{' '}
              <span className={gradientText}>Opposables. Vérifiables.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {candidateCommitments.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.06 }}
                  className="rounded-[1.75rem] bg-white p-8 ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={h3Class}>{item.title}</h3>
                  <p className={bodyClass}>{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 6 — Spécialités */}
      <SectionBackground page="local-paca" section="lp-specialites" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Nos spécialités en PACA</span>
            <h2 className={h2Class}>
              Les fonctions où la qualité du recrutement{' '}
              <span className={gradientText}>change la performance.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {specialites.map((s, index) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.06 }}
                  className="rounded-[1.75rem] bg-[#fbfaf8] p-8 ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={h3Class}>{s.title}</h3>
                  <p className={bodyClass}>{s.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 7 — Double dividende */}
      <SectionBackground page="local-paca" section="lp-dividend" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Le bénéfice qui paie deux fois</span>
            <h2 className={h2Class}>
              Vous embauchez 1 candidat.{' '}
              <span className={gradientText}>Vous gagnez 79 ambassadeurs régionaux.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {dividendStats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.metric}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className="rounded-[1.75rem] bg-white p-8 ring-1 ring-neutral-200 shadow-sm"
                >
                  <div className="mb-5 flex items-center justify-between">
                    <div className="h-12 w-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white shadow-lg shadow-[#d03484]/20">
                      <Icon className="w-6 h-6" />
                    </div>
                    <p className="text-3xl font-bold text-[#30475e] tracking-tight">{stat.metric}</p>
                  </div>
                  <p className={bodyClass}>{stat.text}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 8 — Comparatif */}
      <SectionBackground page="local-paca" section="lp-vs" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Recruteurs.io vs cabinet classique</span>
            <h2 className={h2Class}>
              Ce qui différencie notre approche n’est pas le discours.{' '}
              <span className={gradientText}>C’est ce que nous faisons à chaque étape.</span>
            </h2>
          </div>

          <div className="max-w-5xl mx-auto rounded-[2rem] ring-1 ring-neutral-200 overflow-hidden bg-white">
            <div className="hidden md:grid grid-cols-[1fr_1fr_1fr] bg-neutral-900 text-white">
              <div className="p-5 text-xs font-semibold uppercase tracking-widest">Critère</div>
              <div className="p-5 text-xs font-semibold uppercase tracking-widest bg-gradient-to-r from-[#d03484] to-[#d47254]">recruteurs.io</div>
              <div className="p-5 text-xs font-semibold uppercase tracking-widest text-neutral-400">Cabinet classique</div>
            </div>

            {comparatif.map((row, idx) => (
              <div
                key={row.critere}
                className={`grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr] ${
                  idx !== comparatif.length - 1 ? 'border-b border-neutral-100' : ''
                } ${idx % 2 === 0 ? 'bg-white' : 'bg-[#fbfaf8]'}`}
              >
                <div className="p-5 md:p-6">
                  <p className="text-sm font-semibold text-neutral-500 uppercase tracking-wider md:hidden mb-1">Critère</p>
                  <p className="text-base font-semibold text-[#30475e]">{row.critere}</p>
                </div>
                <div className="p-5 md:p-6 bg-gradient-to-r from-[#fde7f2]/40 to-[#fde2d3]/40">
                  <p className="text-sm font-semibold text-[#d03484] uppercase tracking-wider md:hidden mb-1">recruteurs.io</p>
                  <span className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#d03484] flex-shrink-0 mt-0.5" />
                    <span className="text-base font-semibold text-[#30475e]">{row.recruteurs}</span>
                  </span>
                </div>
                <div className="p-5 md:p-6">
                  <p className="text-sm font-semibold text-neutral-400 uppercase tracking-wider md:hidden mb-1">Cabinet classique</p>
                  <p className="text-base text-neutral-500">{row.classique}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 9 — Garantie */}
      <SectionBackground page="local-paca" section="lp-guarantee" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Notre garantie</span>
            <h2 className={h2Class}>
              Si ça ne tient pas dans les 6 mois,{' '}
              <span className={gradientText}>on recommence.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {guaranteeBullets.map((g, index) => {
              const Icon = g.icon;
              return (
                <motion.div
                  key={g.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className="rounded-[1.75rem] bg-white p-8 ring-1 ring-neutral-200 shadow-sm"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={h3Class}>{g.title}</h3>
                  <p className={bodyClass}>{g.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 10 — Logos clients (wall créatif partagé) */}
      <ClientLogosWall page="local-paca" section="lp-trust" />

      {/* SECTION 11 — Vidéo */}
      <SectionBackground page="local-paca" section="lp-press" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-12">
            <span className={eyebrowClass}><PlayCircle className="w-4 h-4 mr-2" />Vu à la télé</span>
            <h2 className={h2Class}>
              Notre vision du recrutement,{' '}
              <span className={gradientText}>expliquée à l’antenne.</span>
            </h2>
          </div>

          <div className="max-w-4xl mx-auto rounded-[2rem] overflow-hidden ring-1 ring-neutral-200 shadow-xl">
            <div className="relative w-full" style={{ paddingBottom: '56.25%', height: 0 }}>
              <iframe
                src="https://www.youtube.com/embed/sXY1BChmGSc"
                title="recruteurs.io passe à la télé"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
                className="absolute top-0 left-0 w-full h-full"
              />
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 12 — FAQ */}
      <SectionBackground page="local-paca" section="lp-faq" className="bg-white">
        <Section>
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className={eyebrowClass}>Questions fréquentes</span>
            <h2 className={h2Class}>
              Sur le recrutement{' '}
              <span className={gradientText}>en PACA.</span>
            </h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-3">
            {faqItems.map((item, i) => (
              <FAQItem key={i} question={item.question} answer={item.answer} />
            ))}
          </div>

          <div className="mt-12 text-center">
            <a href="/infos/questions" className="font-semibold text-[#d03484] underline-offset-4 hover:underline">
              Voir toutes les questions fréquentes →
            </a>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 13 — CTA */}
      <SectionBackground page="local-paca" section="lp-cta" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className={h2Class}>
              Parlons de votre recrutement{' '}
              <span className={gradientText}>en PACA.</span>
            </h2>
            <p className={`${sectionSubtitleClass} mb-10`}>
              Nous intervenons sur les six départements PACA. Une conversation directe, sans engagement,
              pour cadrer ensemble votre besoin.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => navigate('/reservation')}
                className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-2.5 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-[#d03484]/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                Réserver un échange (15 min)
              </button>
              <button
                onClick={() => setIsHubSpotModalOpen(true)}
                className="inline-flex items-center gap-2 rounded-2xl bg-white text-[#30475e] px-6 py-2.5 text-sm font-bold whitespace-nowrap ring-1 ring-neutral-200 hover:ring-neutral-300 hover:-translate-y-0.5 transition-all"
              >
                Nous écrire
              </button>
            </div>
          </div>
        </Section>
      </SectionBackground>

      <HubSpotModal isOpen={isHubSpotModalOpen} onClose={() => setIsHubSpotModalOpen(false)} />
    </Layout>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-2xl ring-1 ring-neutral-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left px-6 py-5 flex items-center justify-between gap-4 hover:bg-neutral-50 transition-colors"
        aria-expanded={open}
      >
        <span className="font-semibold text-[#30475e] text-base leading-snug">{question}</span>
        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white">
          {open ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </span>
      </button>
      {open && (
        <div className="px-6 pb-5 text-neutral-700 leading-relaxed border-t border-neutral-100 pt-4 text-base">
          {answer}
        </div>
      )}
    </div>
  );
}
